/**
 * A hand of up to three choice cards fanned like held cards, the middle one
 * upright and lowest. One layout for the reader's quiz drawer and retells'
 * reference gate.
 *
 * Props-only and unstyled: it owns the fan transforms, the lift of a selected
 * card, the stagger delay and the stack fallback's transform; the shell owns
 * the card itself (`renderCard`), the cooldown styling and whether the row
 * stacks. The stack decision is a plain boolean so each shell plugs in its own
 * measure (`stackFromLength`, `stackFromWidth`, or its own).
 */

import type { CSSProperties, JSX, ReactNode, Ref } from "react";

interface FanPose {
  rotate: string;
  y: string;
  x: string;
}

const UPRIGHT: FanPose = { rotate: "0deg", y: "4px", x: "0px" };
const SPARE: FanPose = { rotate: "0deg", y: "-4px", x: "0px" };
const FAN: readonly FanPose[] = [
  { rotate: "6deg", y: "-4px", x: "-4px" },
  UPRIGHT,
  { rotate: "-6deg", y: "-4px", x: "4px" },
];
const LIFT = "translateY(-4px)";

export function fanTransform({
  index,
  stacked,
  lifted,
}: {
  index: number;
  stacked: boolean;
  lifted: boolean;
}): string {
  if (stacked) return lifted ? LIFT : "none";
  if (lifted) return `rotate(0deg) ${LIFT}`;
  const pose = FAN[index] ?? SPARE;
  return `rotate(${pose.rotate}) translateY(${pose.y}) translateX(${pose.x})`;
}

export function staggerDelayMs({
  index,
  active,
  stepMs,
}: {
  index: number;
  active: boolean;
  stepMs: number;
}): number {
  return active ? index * stepMs : 0;
}

/** Stack once any choice is at least `threshold` characters long. */
export function stackFromLength(
  choices: readonly string[],
  threshold: number,
): boolean {
  return choices.some((choice) => choice.length >= threshold);
}

/** Stack once the row's natural width no longer fits its container. */
export function stackFromWidth(
  naturalWidth: number,
  availableWidth: number,
): boolean {
  return (
    naturalWidth > 0 && availableWidth > 0 && naturalWidth > availableWidth
  );
}

export interface ChoiceFanCardState {
  index: number;
  stacked: boolean;
  lifted: boolean;
  /** Stagger delay for this card, in ms; 0 when the stagger is inactive. */
  staggerDelayMs: number;
}

export interface ChoiceFanProps<T> {
  choices: readonly T[];
  getKey: (choice: T, index: number) => string | number;
  isSelected: (choice: T) => boolean;
  /** A selected card lifts only while this is false (e.g. once confirmed or locked). */
  locked: boolean;
  /** Whether the row falls back to a vertical stack, with no rotation. */
  stacked: boolean;
  stagger?: { active: boolean; stepMs: number };
  /** Appended to each card's transform, e.g. `translateY(8px)`. */
  sink?: (state: ChoiceFanCardState) => string;
  renderCard: (choice: T, state: ChoiceFanCardState) => ReactNode;
  containerRef?: Ref<HTMLDivElement>;
  containerProps?: Omit<JSX.IntrinsicElements["div"], "ref" | "children">;
  cardClassName?: (state: ChoiceFanCardState) => string | undefined;
  cardStyle?: (state: ChoiceFanCardState) => CSSProperties | undefined;
}

export function ChoiceFan<T>({
  choices,
  getKey,
  isSelected,
  locked,
  stacked,
  stagger,
  sink,
  renderCard,
  containerRef,
  containerProps,
  cardClassName,
  cardStyle,
}: ChoiceFanProps<T>): JSX.Element {
  return (
    <div {...containerProps} ref={containerRef}>
      {choices.map((choice, index) => {
        const lifted = isSelected(choice) && !locked;
        const state: ChoiceFanCardState = {
          index,
          stacked,
          lifted,
          staggerDelayMs: staggerDelayMs({
            index,
            active: stagger?.active ?? false,
            stepMs: stagger?.stepMs ?? 0,
          }),
        };
        const base = fanTransform({ index, stacked, lifted });
        const extra = sink?.(state);
        const transform =
          extra === undefined
            ? base
            : base === "none"
              ? extra
              : `${base} ${extra}`;
        return (
          <div
            key={getKey(choice, index)}
            className={cardClassName?.(state)}
            style={{ ...cardStyle?.(state), transform }}
          >
            {renderCard(choice, state)}
          </div>
        );
      })}
    </div>
  );
}
